// ======================================================================
// File: POFromRFQDrawer.js
// Module: PPE → Procurement → Purchase Order
//
// Purpose:
// - Create Purchase Order directly from FINALIZED RFQ
// - Select RFQ and auto-generate PO
// - Preview RFQ data before creation
// - Uses correct axiosClient path as per project structure
// ======================================================================

import React, { useEffect, useState } from "react";

// CORRECT PATH TO axiosClient (inside src/api)
import axiosClient from "../../../../../api/axiosClient";

import purchaseOrderService from "./services/purchaseOrderService";

const POFromRFQDrawer = ({ onClose,
  onPOCreated }) => {

  const [rfqs, setRfqs] = useState([]);
  const [selectedRFQ, setSelectedRFQ] = useState("");
  const [rfqDetail, setRfqDetail] = useState(null);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // -------------------------------------------------------------
  // LOAD FINALIZED RFQs
  // -------------------------------------------------------------
  const loadRFQs = async () => {
    try {
      setLoading(true);

      const res = await axiosClient.get(
        "/api/ppe/material-flow/rfq"
      );

      if (res.data?.success) {

  const rfqList = res.data?.data?.data || [];
    console.log("RFQ API Response", res.data);    
    const finalized = rfqList.filter(
      (r) => r.status_code === "FINALIZED"
    );

    setRfqs(finalized);
  }
    } catch (err) {
      alert("Failed to load RFQs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRFQs();
  }, []);

  // -------------------------------------------------------------
  // LOAD SELECTED RFQ DETAIL
  // -------------------------------------------------------------
  const loadRFQDetail = async (rfqId) => {
    if (!rfqId) {
      setRfqDetail(null);
      return;
    }

    try {
      setLoading(true);

      const res = await axiosClient.get(
        `/api/ppe/material-flow/rfq/${rfqId}`
      );

      if (res.data?.success) {
        setRfqDetail(res.data.data);
      }
    } catch (err) {
      alert("Failed to load RFQ detail");
    } finally {
      setLoading(false);
    }
  };

  const handleRFQChange = (e) => {
    const id = e.target.value;
    setSelectedRFQ(id);
    loadRFQDetail(id);
  };

  // -------------------------------------------------------------
  // CREATE PO FROM RFQ
  // -------------------------------------------------------------
  const handleCreatePO = async () => {
    if (!selectedRFQ) {
      alert("Please select an RFQ");
      return;
    }

    if (!window.confirm("Create Purchase Order from this RFQ?")) return;

    try {
      setSaving(true);

      const res = await purchaseOrderService.createFromRFQ(selectedRFQ);

      if (res.success) {

        alert("Purchase Order created successfully");

        const poId = res.data?.id;

        if (onPOCreated && poId) {

          onPOCreated(poId);

        } else {

          onClose();

        }
      }
    } catch (err) {
      alert(err);
    } finally {
      setSaving(false);
    }
  };
  console.log("POFromRFQDrawer Rendered");
  // -------------------------------------------------------------
  // RENDER
  // -------------------------------------------------------------
  return (
    <div
  style={{
    position: "fixed",
    top: 0,
    right: 0,
    width: "900px",
    height: "100vh",
    background: "#fff",
    zIndex: 99999,
    overflowY: "auto",
    borderLeft: "1px solid #ddd",
    padding: "20px"
  }}
>

      <div className="drawer-header">
        <h5>Create Purchase Order from RFQ</h5>
        <button className="btn-close" onClick={onClose}>×</button>
      </div>

      <div className="drawer-body">

        {loading && <p>Loading...</p>}

        {/* RFQ SELECTION */}
        <div className="row mb-3">
          <div className="col-md-12">
            <label>Select Finalized RFQ</label>

            <select
              className="form-control"
              value={selectedRFQ}
              onChange={handleRFQChange}
            >
              <option value="">-- Select RFQ --</option>

              {rfqs.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.rfq_no} - {r.rfq_date}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* PREVIEW SECTION */}
        {rfqDetail && (
          <>
            <hr />

            <h6>RFQ Information</h6>

            <div className="row mb-3">
              <div className="col-md-6">
                <label>RFQ No</label>
                <div className="form-control">
                  {rfqDetail.rfq_no}
                </div>
              </div>

              <div className="col-md-6">
                <label>Date</label>
                <div className="form-control">
                  {rfqDetail.rfq_date}
                </div>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-6">
                <label>Finalized Vendor</label>
                <div className="form-control">
                  {rfqDetail.finalized_vendor_name || "N/A"}
                </div>
              </div>
            </div>

            <h6 className="mt-4">Material Lines</h6>

            <table className="table table-bordered">
              <thead>
                <tr>
                  <th>Material</th>
                  <th>Qty</th>
                  <th>UOM</th>
                  <th>Unit Rate</th>
                  <th>Unit Tax</th>
                  <th>Unit Total</th>
                  <th>Line Total</th>
                </tr>
              </thead>

              <tbody>
                {rfqDetail.lines?.map((l, i) => (
                  <tr key={i}>
                    <td>{l.material_name}</td>

                    <td>
                      {Number(l.qty || 0).toFixed(2)}
                    </td>

                    <td>{l.uom}</td>

                    <td>
                      {Number(l.final_rate || 0).toFixed(2)}
                    </td>

                    <td>
                      {Number(l.unit_tax || 0).toFixed(2)}
                    </td>

                    <td>
                      {Number(l.final_total || 0).toFixed(2)}
                    </td>

                    <td>
                      {Number(l.line_total || 0).toFixed(2)}
                    </td>
                  </tr>
                ))}

                {(!rfqDetail.lines || rfqDetail.lines.length === 0) && (
                  <tr>
                    <td colSpan="5">No materials found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </>
        )}
      </div>

      <div className="drawer-footer">
        <button
          className="btn btn-secondary me-2"
          onClick={onClose}
        >
          Cancel
        </button>

        <button
          className="btn btn-primary"
          disabled={!selectedRFQ || saving}
          onClick={handleCreatePO}
        >
          {saving ? "Creating..." : "Create PO"}
        </button>
      </div>
    </div>
  );
};

export default POFromRFQDrawer;
